import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowDownWideNarrow, Search } from "lucide-react";

const c = {
  greenMd: "#2D6B45",
  gold: "#E8A020",
  ink: "#0F1F17",
  muted: "#6B7D74",
  surface: "#F4F6F5",
  border: "rgba(28,74,46,0.1)",
  white: "#FFFFFF",
};

interface Producto {
  producto_id: number;
  nombre: string;
  articulo: string;
  precio: number;
  estado: string;
}

type Filter = "todos" | "alta";
type SortMode = "name" | "pts";

function formatPts(n: number) {
  return n.toLocaleString("es-CR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const ProductosList = () => {
  const [productos, setProductos] = useState<Producto[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState<Filter>("todos");
  const [sortMode, setSortMode] = useState<SortMode>("name");

  const getProductos = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/producto_farmacia/farmacia/${sessionStorage.getItem("user_id")}`,
        {
          headers: {
            "auth-token": `${sessionStorage.getItem("token")}`,
          },
        }
      );
      const raw: Producto[] = Array.isArray(response.data) ? response.data : [];
      const onlyActive = raw.filter(
        (p) => (p.estado || "").toLowerCase() === "activo"
      );
      setProductos(onlyActive);
    } catch (error) {
      console.log(error);
      setProductos([]);
    }
  }, []);

  useEffect(() => {
    getProductos();
  }, [getProductos]);

  const medianPrecio = useMemo(() => {
    if (productos.length === 0) return 0;
    const s = [...productos].map((p) => p.precio).sort((a, b) => a - b);
    return s[Math.floor(s.length / 2)] ?? 0;
  }, [productos]);

  const visible = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    let list = productos.filter((p) => {
      if (q) {
        const ok =
          p.nombre.toLowerCase().includes(q) ||
          p.articulo.toLowerCase().includes(q);
        if (!ok) return false;
      }
      if (filter === "alta") {
        return p.precio >= medianPrecio;
      }
      return true;
    });
    if (sortMode === "name") {
      list = [...list].sort((a, b) =>
        a.nombre.localeCompare(b.nombre, "es", { sensitivity: "base" })
      );
    } else {
      list = [...list].sort((a, b) => b.precio - a.precio);
    }
    return list;
  }, [productos, searchTerm, filter, sortMode, medianPrecio]);

  const toggleSort = () => {
    setSortMode((m) => (m === "name" ? "pts" : "name"));
  };

  return (
    <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col">
      <div
        className="mx-auto flex min-h-0 w-full min-w-0 max-w-full flex-1 flex-col overflow-hidden md:max-w-6xl md:rounded-xl md:border md:border-gray-200/90 md:shadow-sm lg:max-w-7xl"
        style={{ background: c.surface }}
      >
        <div
          className="flex-shrink-0 border-b border-black/[0.06] bg-white px-3.5 pb-2.5 pt-3 md:px-6 md:pt-4"
        >
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2"
              style={{ color: c.muted }}
            />
            <input
              type="search"
              placeholder="Buscar por nombre o código…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-xl border-[1.5px] py-2.5 pl-8 pr-3 text-sm text-[0.8rem] text-[#0F1F17] outline-none transition focus:border-[#2D6B45] focus:ring-[3px] focus:ring-[#2D6B45]/10"
              style={{
                borderColor: c.border,
                background: c.white,
              }}
            />
          </div>
          <div
            className="mt-2.5 flex gap-1.5 overflow-x-auto pb-0.5 [scrollbar-width:none] md:flex-wrap md:overflow-x-visible [&::-webkit-scrollbar]:hidden"
            role="tablist"
            aria-label="Filtro de listado"
          >
            {(
              [
                { id: "todos" as const, label: "Todos" },
                { id: "alta" as const, label: "Puntos altos" },
              ] as const
            ).map((f) => (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={filter === f.id}
                onClick={() => setFilter(f.id)}
                className="flex-shrink-0 rounded-full border border-[1.5px] px-3 py-1.5 text-[0.7rem] font-semibold transition"
                style={{
                  borderColor: filter === f.id ? c.greenMd : c.border,
                  background: filter === f.id ? c.greenMd : "transparent",
                  color: filter === f.id ? c.white : c.muted,
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-shrink-0 items-center justify-between px-3.5 py-0.5 md:px-6">
          <span
            className="text-[0.58rem] font-semibold uppercase tracking-widest sm:text-xs"
            style={{ color: c.muted }}
          >
            {visible.length}{" "}
            {visible.length === 1 ? "producto" : "productos"}
          </span>
          <button
            type="button"
            onClick={toggleSort}
            className="inline-flex items-center gap-1.5 text-[0.6rem] font-semibold sm:text-xs"
            style={{ color: c.greenMd }}
          >
            <ArrowDownWideNarrow className="h-3.5 w-3.5" />
            {sortMode === "name" ? "Nombre A→Z" : "Puntos (mayor a menor)"}
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-3.5 pb-3 md:px-6 md:pb-4 flex flex-col gap-1.5 md:grid md:grid-cols-2 md:content-start md:gap-3 lg:grid-cols-3">
          {visible.length === 0 ? (
            <div className="flex flex-col items-center py-10 text-center md:col-span-full">
              <Search className="h-9 w-9" style={{ color: c.muted }} aria-hidden />
              <p className="mt-1.5 text-sm font-bold" style={{ color: c.ink }}>
                Sin resultados
              </p>
              <p className="mt-1 text-xs leading-relaxed" style={{ color: c.muted }}>
                No hay productos que coincidan
                <br />
                con tu búsqueda.
              </p>
            </div>
          ) : (
            visible.map((p, idx) => {
              return (
                <div
                  key={p.producto_id}
                  className="h-fit min-w-0 overflow-hidden rounded-2xl border border-[1.5px] bg-white"
                  style={{
                    borderColor: c.border,
                    animation: `pcSlide 0.28s ease both`,
                    animationDelay: `${Math.min(idx, 12) * 0.04}s`,
                  }}
                >
                  <div className="flex w-full min-w-0 items-start gap-3 px-3.5 py-2.5">
                    <div className="min-w-0 flex-1">
                      <div
                        className="mb-0.5 inline-block rounded bg-[#E8EDEA] px-1.5 py-px text-[0.5rem] font-bold uppercase tracking-widest"
                        style={{ color: c.muted }}
                      >
                        {p.articulo}
                      </div>
                      <div
                        className="break-words text-[0.8125rem] font-bold leading-snug"
                        style={{ color: c.ink }}
                      >
                        {p.nombre}
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-0.5">
                      <div
                        className="flex items-center gap-0.5 text-xs font-extrabold"
                        style={{ color: c.gold }}
                      >
                        <span
                          className="flex h-4 w-4 items-center justify-center rounded-full text-[0.45rem] text-white"
                          style={{ background: c.gold }}
                        >
                          ¢
                        </span>
                        {formatPts(p.precio)}
                      </div>
                      <div
                        className="flex items-center gap-0.5 text-[0.58rem] font-semibold"
                        style={{ color: c.muted }}
                      >
                        <span
                          className="h-2 w-2 shrink-0 rounded-full"
                          style={{ background: "#22C55E" }}
                        />
                        Activo
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <style>
        {`
        @keyframes pcSlide {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: none; }
        }
        `}
      </style>
    </div>
  );
};

export default ProductosList;
